// ============================================================
// FEATURE: PowerMonitor — a cross-window RECEIVER / monitor wall.
//
// This program produces NO output of its own. Everything it shows is
// driven by OTHER windows over the shared windowBus:
//   * the six rail lamps light from PatchBay's "power.rails" + "power.master"
//   * a SURGE arriving on "power.surge" flares the live lamps once
//   * the big needle gauge swings to the Transmitter's "signal.freq" dial
//   * a SPECIMEN launched on "signal.specimen" crawls across the readout
//
// Open the Annex in a second browser window, drive a PatchBay or a
// Transmitter over there, and watch THIS wall react live with no shared
// React parent — the bus carries it across the gap via localStorage.
//
// Every subscription is torn down on unmount; the surge flare uses a
// single self-clearing timeout that is cancelled on unmount too.
//
// Non-draggable content component — mount as the artComp of an
// AnnexWindow.
// ============================================================

import * as React from "react";
import "./powermonitor.css";
import windowBus from "../_shared/windowBus";
import { tactile, payoff } from "../_shared/feedback";

const RAIL_COUNT = 6;

// How long the spectacular overload eruption holds before it settles back.
// Kept time-boxed (~1.4s) so it stays a punchy payoff, never an idle loop.
const OVERLOAD_MS = 1400;

class PowerMonitor extends React.Component {
  constructor(props) {
    super(props);

    // Seed from whatever the bus already knows so a monitor opened AFTER the
    // senders isn't blank. getState reads the in-memory mirror and falls back
    // to localStorage, so a second window picks up the live system at once.
    const master = windowBus.getState("power.master");
    const rails = windowBus.getState("power.rails");
    const freq = windowBus.getState("signal.freq");

    this.state = {
      master: typeof master === "boolean" ? master : false,
      rails: Array.isArray(rails) ? normalizeRails(rails) : new Array(RAIL_COUNT).fill(false),
      freq: typeof freq === "number" ? clamp01(freq) : 0,
      surging: false,    // brief flare while a surge is being shown
      specimen: null,    // { kind, key } of a specimen currently crawling across
      // --- spectacular overload eruption ---
      overload: false,   // true for ~1.4s while the wall erupts
      overloadKey: 0,    // bumps per eruption so every animation replays
      overloadSource: "", // "surge" | "transmit" — flavors the burst label
      overloadIntensity: 1, // 0..1, drives flash strength + needle overshoot
      pinned: false,     // while true the needle is SLAMMED to max and held
    };

    this._unsubs = [];        // unsubscribe fns, drained on unmount
    this._surgeTimer = null;  // self-clearing surge-flare timeout
    this._specimenTimer = null; // self-clearing specimen-arrival timeout
    this._specimenKey = 0;    // bumps per arrival so the crawl animation restarts
    this._overloadTimer = null;   // clears the eruption back to calm
    this._unpinTimer = null;      // releases the pinned needle mid-eruption
    this._overloadKey = 0;        // bumps per eruption to restart CSS animations
  }

  // Give one tactile answer when something lands on this wall from across the
  // gap, so the receiver feels the remote control too. props.sound is threaded
  // from the Annex's global mute; when false the audio blip is skipped but the
  // haptic may still fire.
  _feedback(kind) {
    tactile({ kind, sound: this.props.sound !== false });
  }

  componentDidMount() {
    // Wire up the cross-window subscriptions. Each returns an unsubscribe fn
    // that we stash for cleanup. Handlers are defensive about value shape.
    this._unsubs.push(
      windowBus.subscribe("power.master", (v) => {
        this.setState({ master: typeof v === "boolean" ? v : false });
      })
    );
    this._unsubs.push(
      windowBus.subscribe("power.rails", (v) => {
        this.setState({ rails: Array.isArray(v) ? normalizeRails(v) : new Array(RAIL_COUNT).fill(false) });
      })
    );
    this._unsubs.push(
      windowBus.subscribe("signal.freq", (v) => {
        this.setState({ freq: typeof v === "number" ? clamp01(v) : 0 });
      })
    );
    this._unsubs.push(
      windowBus.subscribe("power.surge", (v) => {
        // A surge is a one-shot stamp; flare the wall briefly, then settle.
        if (!v || typeof v !== "object") return;
        this.flareSurge();
      })
    );
    this._unsubs.push(
      windowBus.subscribe("signal.specimen", (v) => {
        // A launched specimen arrives from the Transmitter window — send it
        // crawling across the readout, then clear it.
        if (!v || typeof v !== "object") return;
        this.receiveSpecimen(typeof v.kind === "string" ? v.kind : "SPECIMEN");
      })
    );
    this._unsubs.push(
      windowBus.subscribe("power.overload", (v) => {
        // The big one: a TRANSMIT or SURGE activation lands. Erupt the wall.
        if (!v || typeof v !== "object") return;
        this.eruptOverload(v);
      })
    );
  }

  componentWillUnmount() {
    // Drain every subscription so no handler fires after we're gone.
    for (let i = 0; i < this._unsubs.length; i++) {
      try {
        this._unsubs[i]();
      } catch (e) {
        /* ignore */
      }
    }
    this._unsubs = [];
    if (this._surgeTimer != null) {
      window.clearTimeout(this._surgeTimer);
      this._surgeTimer = null;
    }
    if (this._specimenTimer != null) {
      window.clearTimeout(this._specimenTimer);
      this._specimenTimer = null;
    }
    if (this._overloadTimer != null) {
      window.clearTimeout(this._overloadTimer);
      this._overloadTimer = null;
    }
    if (this._unpinTimer != null) {
      window.clearTimeout(this._unpinTimer);
      this._unpinTimer = null;
    }
  }

  // ----------------------------------------------------------------------
  // The spectacular ACTIVATION payoff. A TRANSMIT or SURGE from another window
  // arrives on "power.overload" and the whole wall ERUPTS for ~1.4s:
  //   * a full-panel overload flash slams on, then fades
  //   * the needle is SLAMMED past full-scale and PINNED, then released to
  //     settle back to the live dial value
  //   * a shockwave ring + an electric arc + a signal sweep race across
  //   * every lamp blows white-hot
  //   * the readouts spike (handled in render off the pinned/overload flags)
  // All of it is pure CSS transform/opacity, time-boxed, and reduced-motion
  // gets a calmer-but-clear version (no slam/streak, just a brief flash).
  // It rides ON TOP of the existing surge/specimen reactions, never replacing
  // them, and answers in the hand with the bigger payoff() burst.
  // ----------------------------------------------------------------------
  eruptOverload(stamp) {
    const source =
      stamp && typeof stamp.source === "string" ? stamp.source : "surge";
    const intensity =
      stamp && typeof stamp.intensity === "number"
        ? clamp01(stamp.intensity)
        : 1;

    // Punchier haptic + layered discharge burst on the receiver too, so the
    // window watching the wall feels the hit, not just the one that fired it.
    payoff({ kind: source === "transmit" ? "transmit" : "surge", sound: this.props.sound !== false });

    // Restart any in-flight eruption cleanly so rapid presses re-trigger.
    if (this._overloadTimer != null) window.clearTimeout(this._overloadTimer);
    if (this._unpinTimer != null) window.clearTimeout(this._unpinTimer);

    this._overloadKey += 1;
    this.setState({
      overload: true,
      pinned: true,
      overloadKey: this._overloadKey,
      overloadSource: source,
      overloadIntensity: intensity,
    });

    // Release the pinned needle partway through so it visibly RECOILS and
    // settles back to the real dial value instead of snapping instantly.
    this._unpinTimer = window.setTimeout(() => {
      this._unpinTimer = null;
      this.setState({ pinned: false });
    }, Math.round(OVERLOAD_MS * 0.45));

    // End the eruption and return the wall to its calm metering state.
    this._overloadTimer = window.setTimeout(() => {
      this._overloadTimer = null;
      this.setState({ overload: false });
    }, OVERLOAD_MS);
  }

  receiveSpecimen(kind) {
    // A specimen landing from the Transmitter window reads as an arrival blip.
    this._feedback("transmit");
    if (this._specimenTimer != null) {
      window.clearTimeout(this._specimenTimer);
    }
    // Bump the key so React remounts the crawler and the CSS animation replays
    // even when the same specimen kind arrives twice in a row.
    this._specimenKey += 1;
    this.setState({ specimen: { kind, key: this._specimenKey } });
    this._specimenTimer = window.setTimeout(() => {
      this._specimenTimer = null;
      this.setState({ specimen: null });
    }, 2600);
  }

  flareSurge() {
    // A surge arriving from the PatchBay window jolts the wall — answer it.
    this._feedback("surge");
    if (this._surgeTimer != null) {
      window.clearTimeout(this._surgeTimer);
    }
    this.setState({ surging: true });
    this._surgeTimer = window.setTimeout(() => {
      this._surgeTimer = null;
      this.setState({ surging: false });
    }, 600);
  }

  render() {
    const {
      master,
      rails,
      freq,
      surging,
      specimen,
      overload,
      overloadKey,
      overloadSource,
      overloadIntensity,
      pinned,
    } = this.state;

    // The gauge needle sweeps across a 240° arc (-120° .. +120°) by freq.
    // While PINNED in an eruption it is SLAMMED past full scale (+132°) and
    // held there, then released to settle back to the real dial value.
    const restAngle = -120 + freq * 240;
    const angle = pinned ? 132 : restAngle;
    // The numeric readout spikes to a pegged "OVL" reading while pinned.
    const pct = pinned ? 100 : Math.round(freq * 100);

    const wallClass =
      "pm-wall" +
      (master ? " pm-armed" : " pm-open") +
      (surging ? " pm-surging" : "") +
      (overload ? " pm-overload" : "") +
      (pinned ? " pm-pinned" : "") +
      (overloadSource ? " pm-ovl-" + overloadSource : "");

    // Intensity (0..1) is exposed as a CSS var so the flash / shockwave scale
    // with how hard the activation hit, without re-authoring keyframes.
    const wallStyle = { "--pm-ovl": overloadIntensity };

    return (
      <div className={wallClass} style={wallStyle}>
        {/* full-panel eruption overlay — flash + shockwave + arc + sweep.
            Keyed so every activation restarts all of its animations. Pointer-
            transparent so it never blocks the controls beneath. */}
        {overload && (
          <div key={overloadKey} className="pm-erupt" aria-hidden="true">
            <span className="pm-erupt-flash" />
            <span className="pm-erupt-shock" />
            <span className="pm-erupt-arc" />
            <span className="pm-erupt-sweep" />
            <span className="pm-erupt-tag">
              {overloadSource === "transmit" ? "◈ SIGNAL" : "↯ OVERLOAD"}
            </span>
          </div>
        )}

        <div className="pm-strip">
          <span className="pm-strip-dot" aria-hidden="true" />
          <span className="pm-strip-text">
            REMOTE FEED &middot;{" "}
            {overload ? "OVERLOAD" : master ? "ARMED" : "NO BUS"}
          </span>
        </div>

        {/* needle gauge — driven by the Transmitter dial in another window */}
        <div className="pm-gauge">
          <div className="pm-gauge-face">
            <div className="pm-gauge-ticks" aria-hidden="true" />
            <div
              className="pm-needle"
              style={{ transform: "rotate(" + angle + "deg)" }}
              aria-hidden="true"
            />
            <div className="pm-gauge-hub" aria-hidden="true" />
          </div>
          <div className="pm-gauge-read">
            <span className="pm-gauge-pct">{pinned ? "OVL" : pct}</span>
            <span className="pm-gauge-unit">FREQ</span>
          </div>
        </div>

        {/* the rail lamp bank — mirrors PatchBay's six rails */}
        <div className="pm-lamps">
          {rails.map((on, i) => {
            const live = master && on;
            return (
              <div
                key={i}
                className={"pm-lamp-cell" + (live ? " pm-lamp-live" : "")}
                title={"Rail " + (i + 1)}
              >
                <span className="pm-lamp" aria-hidden="true" />
                <span className="pm-lamp-num">{i + 1}</span>
              </div>
            );
          })}
        </div>

        {/* a specimen launched from the Transmitter window crawls through here */}
        {specimen && (
          <div className="pm-specimen-track" aria-live="polite">
            <span key={specimen.key} className="pm-specimen">
              ▣ INBOUND &middot; {specimen.kind}
            </span>
          </div>
        )}

        <div className="pm-foot">FED FROM A SEPARATE WINDOW</div>
      </div>
    );
  }
}

function clamp01(n) {
  if (typeof n !== "number" || !isFinite(n)) return 0;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

function normalizeRails(arr) {
  const out = new Array(RAIL_COUNT).fill(false);
  for (let i = 0; i < RAIL_COUNT; i++) out[i] = !!arr[i];
  return out;
}

export default PowerMonitor;
